import { DefaultTheme, ThemeProvider } from "styled-components";
import { theme } from "./theme";
import React, { useEffect } from "react";
import { cloneDeep, merge } from "lodash";

type DeepPartial<T> = {
    [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

export type ThemeOverride = DeepPartial<DefaultTheme>;

type ThemeOverrideContextValue = {
    themeOverride: ThemeOverride;
    setThemeOverride: (override: ThemeOverride) => void;
};

const ThemeOverrideContext = React.createContext<ThemeOverrideContextValue | null>(null);

export const useThemeOverride = () => {
    const ctx = React.useContext(ThemeOverrideContext);
    if (!ctx) {
        throw new Error("useThemeOverride must be used within AuthoryThemeProvider");
    }
    return ctx;
};

export const AuthoryThemeProvider: React.FC<React.PropsWithChildren<{ initialThemeProps: ThemeOverride }>> = ({
    children,
    initialThemeProps = {}
}) => {
    const [themeOverride, setThemeOverride] = React.useState<ThemeOverride>(initialThemeProps);
    const mergedTheme = React.useMemo(() => ({ ...cloneDeep(theme), ...cloneDeep(themeOverride) }), [themeOverride]) as DefaultTheme;

    const internalThemeOverride = (override: ThemeOverride) => {
        setThemeOverride(merge({}, themeOverride, override));
    };

    useEffect(() => {
        internalThemeOverride(initialThemeProps);
    }, []);

    return (
        <ThemeOverrideContext.Provider value={{ themeOverride, setThemeOverride: internalThemeOverride }}>
            <ThemeProvider theme={mergedTheme}>{children}</ThemeProvider>
        </ThemeOverrideContext.Provider>
    );
};
